// Configuration for open_fund_fields based on Ruby Settings.etf_static_data.open_fund_fields
// This mirrors the configuration in quantizr-admin/config/application.yml

export interface OpenFundFieldConfig {
  code: string;
  type:
    | "string"
    | "boolean"
    | "datepicker"
    | "number"
    | "country"
    | "countries";
  label: string;
  section: string;
}

// Countries list from Ruby app
export const countries = [
  "BE",
  "BG",
  "CZ",
  "DK",
  "DE",
  "EE",
  "IE",
  "IS",
  "GR",
  "ES",
  "FR",
  "HR",
  "IT",
  "CY",
  "LV",
  "LI",
  "LT",
  "LU",
  "HU",
  "MT",
  "NL",
  "NO",
  "AT",
  "PL",
  "PT",
  "RO",
  "SI",
  "SK",
  "FI",
  "SE",
  "CH",
  "GB",
]
  .map((code) => ({ value: code, label: code }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const openFundFields: OpenFundFieldConfig[] = [
  // Fund section
  { code: "OFST005010", type: "string", label: "Umbrella", section: "fund" },
  {
    code: "OFST010500",
    type: "boolean",
    label: "Is Fund Of Funds",
    section: "fund",
  },
  {
    code: "OFST020560",
    type: "datepicker",
    label: "Share Class Launch Date",
    section: "fund",
  },
  {
    code: "OFST010410",
    type: "string",
    label: "Fund Currency",
    section: "fund",
  },
  {
    code: "OFST010210",
    type: "string",
    label: "Portfolio Currency Hedge",
    section: "fund",
  },
  {
    code: "OFST023100",
    type: "string",
    label: "Investment Status",
    section: "fund",
  },
  {
    code: "OFST020400",
    type: "string",
    label: "Share Class Distribution Policy",
    section: "fund",
  },
  {
    code: "OFST011000",
    type: "boolean",
    label: "Has Securities Lending",
    section: "fund",
  },

  // Listing section
  {
    code: "OFST062040",
    type: "string",
    label: "Exchange Place",
    section: "listing",
  },
  {
    code: "OFST010010",
    type: "country",
    label: "Fund Domicile",
    section: "listing",
  },
  {
    code: "OFST062010",
    type: "string",
    label: "Listing Currency",
    section: "listing",
  },
  {
    code: "OFST062000",
    type: "datepicker",
    label: "Listing Date",
    section: "listing",
  },

  // Stakeholders section
  {
    code: "OFST001300",
    type: "string",
    label: "Fund Administrator Name",
    section: "stakeholders",
  },
  {
    code: "OFST001500",
    type: "string",
    label: "Fund Advisor Name",
    section: "stakeholders",
  },
  {
    code: "OFST001100",
    type: "string",
    label: "Fund Promoter Name",
    section: "stakeholders",
  },
  {
    code: "OFST002000",
    type: "string",
    label: "Marketmaker Name",
    section: "stakeholders",
  },
  {
    code: "OFST001600",
    type: "string",
    label: "Auditor Name",
    section: "stakeholders",
  },
  {
    code: "OFST001900",
    type: "string",
    label: "Collateral Manager Name",
    section: "stakeholders",
  },
  {
    code: "OFST011010",
    type: "string",
    label: "Securities Lending Counterparty Name",
    section: "stakeholders",
  },
  {
    code: "OFST011110",
    type: "string",
    label: "Swap Counterparty Name",
    section: "stakeholders",
  },

  // Fees section
  {
    code: "OFST452000",
    type: "number",
    label: "Management Fee Applied",
    section: "fees",
  },
  {
    code: "OFST452110",
    type: "datepicker",
    label: "TER Excluding Performance Fee Date",
    section: "fees",
  },
  {
    code: "OFST452120",
    type: "number",
    label: "TER Including Performance Fee",
    section: "fees",
  },
  {
    code: "OFST452130",
    type: "datepicker",
    label: "TER Including Performance Fee Date",
    section: "fees",
  },
  {
    code: "OFST452100",
    type: "number",
    label: "TER Excluding Performance Fee",
    section: "fees",
  },

  // Legal section
  { code: "OFST160100", type: "string", label: "Legal Form", section: "legal" },
  {
    code: "OFST6030XX",
    type: "countries",
    label: "Country Legal Registration",
    section: "legal",
  },
  {
    code: "OFST6031XX",
    type: "countries",
    label: "Country Marketing Distribution",
    section: "legal",
  },
  {
    code: "OFST6050XX",
    type: "countries",
    label: "Country Specific Restrictions",
    section: "legal",
  },
  {
    code: "OFST800050",
    type: "string",
    label: "EU Savings Directive",
    section: "legal",
  },
];

// Group fields by section for form rendering
export const fieldsBySection = openFundFields.reduce(
  (acc, field) => {
    if (!acc[field.section]) {
      acc[field.section] = [];
    }
    acc[field.section].push(field);
    return acc;
  },
  {} as Record<string, OpenFundFieldConfig[]>,
);

// Section labels
export const sectionLabels = {
  fund: "Fund",
  listing: "Listing",
  stakeholders: "Stakeholders",
  fees: "Fees",
  legal: "Legal",
};

// Translations for field codes to human-readable labels (for NewDiff/Diff components)
export const openFundFieldsTranslations: Record<string, string> = {
  // Fund section
  OFST005010: "Umbrella",
  OFST010500: "Is Fund Of Funds",
  OFST020560: "Share Class Launch Date",
  OFST010410: "Fund Currency",
  OFST010210: "Portfolio Currency Hedge",
  OFST023100: "Investment Status",
  OFST020400: "Share Class Distribution Policy",
  OFST011000: "Has Securities Lending",

  // Listing section
  OFST062040: "Exchange Place",
  OFST010010: "Fund Domicile",
  OFST062010: "Listing Currency",
  OFST062000: "Listing Date",

  // Stakeholders section
  OFST001300: "Fund Administrator Name",
  OFST001500: "Fund Advisor Name",
  OFST001100: "Fund Promoter Name",
  OFST002000: "Marketmaker Name",
  OFST001600: "Auditor Name",
  OFST001900: "Collateral Manager Name",
  OFST011010: "Securities Lending Counterparty Name",
  OFST011110: "Swap Counterparty Name",

  // Fees section
  OFST452000: "Management Fee Applied",
  OFST452110: "TER Excluding Performance Fee Date",
  OFST452120: "TER Including Performance Fee",
  OFST452130: "TER Including Performance Fee Date",
  OFST452100: "TER Excluding Performance Fee",

  // Legal section
  OFST160100: "Legal Form",
  OFST6030XX: "Country Legal Registration",
  OFST6031XX: "Country Marketing Distribution",
  OFST6050XX: "Country Specific Restrictions",
  OFST800050: "EU Savings Directive",

  // Main fields
  strategy_name: "Strategy",
  strategy_id: "Strategy ID",
  benchmark_name: "Benchmark Name",
  replication_methodology: "Replication Methodology",
  sfdr: "SFDR",
  is_funded: "Is Funded",
  is_leveraged: "Is Leveraged",
  is_ucits: "Is UCITS",
  distribution_policy: "Distribution Policy",
};
